import { useEffect, useRef, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, ScanLine, CheckCircle2, XCircle, RefreshCw, User, Building2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AdminScan() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const scannerRef = useRef(null);
  const html5Ref = useRef(null);

  useEffect(() => { api.get("/admin/events?per_page=50&status=published").then((r) => setEvents(r.data.items || r.data)).catch(() => {}); }, []);

  const stop = async () => {
    if (html5Ref.current && scanning) {
      try { await html5Ref.current.stop(); await html5Ref.current.clear(); } catch (e) { /* ignore */ }
    }
    setScanning(false);
  };

  const start = async () => {
    if (!eventId) { toast.error("Sélectionnez d'abord un événement."); return; }
    setResult(null);
    setScanning(true);
    setTimeout(async () => {
      try {
        const q = new Html5Qrcode("scanner-region");
        html5Ref.current = q;
        await q.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decoded) => {
            await stop();
            try {
              const r = await api.post(`/admin/events/${eventId}/check-in`, { token: decoded });
              setResult({ ok: true, ...r.data });
            } catch (err) {
              setResult({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail) || "Erreur" });
            }
          },
          () => {},
        );
      } catch (e) {
        toast.error("Caméra non accessible — vérifiez les autorisations");
        setScanning(false);
      }
    }, 100);
  };

  useEffect(() => () => { stop(); }, []);

  return (
    <PortalLayout sidebar="admin">
      <div className="mx-auto max-w-md">
        <h1 className="text-3xl font-bold text-navy md:text-4xl"><ScanLine className="mr-2 inline h-7 w-7 text-wine-600" />Check-in QR</h1>
        <p className="mt-1 text-sm text-neutral-600">Scannez les badges des participants à l'arrivée.</p>

        <div className="mt-4">
          <label className="text-xs font-bold uppercase text-neutral-600">Événement</label>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="scan-event">
            <option value="">— Choisir un événement —</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.title} · {new Date(e.date).toLocaleDateString("fr")}</option>)}
          </select>
        </div>

        {!scanning && !result && (
          <button onClick={start} disabled={!eventId} className="mt-4 w-full rounded-full bg-wine-600 px-6 py-3 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-40" data-testid="scan-start"><Camera className="mr-2 inline h-4 w-4" />Démarrer le scanner</button>
        )}

        {scanning && (
          <div className="mt-4">
            <div id="scanner-region" ref={scannerRef} className="overflow-hidden rounded-2xl border-4 border-wine-500" data-testid="scan-region" />
            <button onClick={stop} className="mt-3 w-full rounded-full border border-neutral-200 bg-white px-6 py-2 text-xs font-semibold text-neutral-700">Arrêter</button>
          </div>
        )}

        {result && (
          <div className={`mt-4 rounded-2xl border-2 p-5 ${result.ok ? (result.already_checked_in ? "border-amber-300 bg-amber-50" : "border-emerald-400 bg-emerald-50") : "border-rose-400 bg-rose-50"}`} data-testid="scan-result">
            {result.ok ? (
              <>
                <div className={`flex items-center gap-2 text-sm font-bold ${result.already_checked_in ? "text-amber-700" : "text-emerald-700"}`}>
                  <CheckCircle2 className="h-5 w-5" />
                  {result.already_checked_in ? "DÉJÀ ENREGISTRÉ" : "CHECK-IN VALIDÉ"}
                </div>
                <div className="mt-3 space-y-1.5 text-sm text-neutral-800">
                  <div className="flex items-center gap-2"><User className="h-4 w-4 text-neutral-400" /><strong>{result.first_name} {result.last_name}</strong></div>
                  {result.company && <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-neutral-400" />{result.company}</div>}
                  {result.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-neutral-400" />{result.email}</div>}
                </div>
                {result.checked_in_at && <div className="mt-2 text-[10px] text-neutral-500">à {new Date(result.checked_in_at).toLocaleTimeString("fr")}</div>}
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm font-bold text-rose-700"><XCircle className="h-5 w-5" />{result.error}</div>
            )}
            <button onClick={() => { setResult(null); start(); }} className="mt-4 w-full rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="scan-next"><RefreshCw className="mr-1 inline h-3 w-3" />Scanner le suivant</button>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
